// Wave 3 · Public Contact page — enhanced 3h with a proper form + PublicIntake.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell, { usePublicContent } from "../../components/public/PublicShell";
import { Phone, Mail, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import PublicIntake from "../../design-system/intake/PublicIntake";

// R1e-Freeze-A2P — verbatim SMS opt-in disclosure text. Any change here MUST
// bump the backend `terms_version` / `policy_version` constants so consent
// evidence remains auditable at Twilio Trust Hub review time.
export const CONTACT_SMS_DISCLOSURE_TEXT =
  "By checking this box, I agree to receive SMS messages from ERF ENTERPRISES LLC concerning quote requests, appointments, project updates, account notifications and customer support. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Privacy Policy and Terms and Conditions.";

export default function Contact() {
  const c = usePublicContent();
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "", honeypot: "",
    // R1e-Freeze-A2P — SMS opt-in. Never preselected. Submission works either way.
    sms_consent_granted: false,
  });
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/public/website/service-areas").then((r) => setAreas(r.data || [])).catch(() => {});
  }, []);

  const primary = (areas || []).filter((a) => a.tier === "primary").sort((a, b) => a.order - b.order);
  const corridorLine = primary.length >= 2
    ? `${primary[0].state_name} through ${primary[primary.length - 1].state_name} (East Coast corridor)`
    : "Pennsylvania through Florida (East Coast corridor)";

  const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!form.name || !form.email || !form.message) {
      setError("Name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Use /public/leads (Step-B linker path shipped in 3g).
      const body = {
        source: "Website",
        first_name: form.name.split(" ")[0] || form.name,
        last_name: form.name.split(" ").slice(1).join(" ") || " ",
        email: form.email,
        phone: form.phone || null,
        sector: "residential",
        notes: form.subject ? `${form.subject} — ${form.message}` : form.message,
        honeypot: form.honeypot || null,
        attachment_ids: attachmentIds,
        // R1e-Freeze-A2P — only forward the affirmative opt-in state + exact
        // disclosure string shown at consent time.
        sms_consent_granted: !!form.sms_consent_granted,
        sms_consent_disclosure_text: form.sms_consent_granted ? CONTACT_SMS_DISCLOSURE_TEXT : null,
      };
      const r = await api.post("/public/leads", body);
      setSuccess(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail
             : detail?.message || "Something went wrong. Please try again or call us directly.");
    } finally { setSubmitting(false); }
  }

  const hero = (
    <section className="erf-public-hero" style={{ padding: "72px 0 56px" }} data-testid="contact-hero">
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">Contact</div>
        <h1 style={{ maxWidth: 800 }}>Get in touch.</h1>
        <p className="lead">Every conversation starts here. Add plans or photos to help us respond faster.</p>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <section className="erf-public-section" data-testid="contact-body">
        <div className="erf-public-container erf-contact-grid" data-testid="contact-grid">
          <div>
            <div className="erf-public-eyebrow">Direct</div>
            <div style={{ display: "grid", gap: 20, marginTop: 20 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <Phone size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Office</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: "var(--exec-primary)" }}>{c.contact_phone_office}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <Phone size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Mobile</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: "var(--exec-primary)" }}>{c.contact_phone_mobile}</div>
                </div>
              </div>
              {c.contact_email && (
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <Mail size={20} color="var(--exec-accent)" />
                  <div>
                    <div style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Email</div>
                    <div style={{ fontSize: 16, fontWeight: 500, color: "var(--exec-primary)" }}>{c.contact_email}</div>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <MapPin size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Service area</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: "var(--exec-primary)" }} data-testid="contact-corridor-line">{corridorLine}</div>
                </div>
              </div>
            </div>
            <p style={{ marginTop: 24, fontSize: 13, color: "var(--exec-body)" }}>
              Prefer to describe your project in detail? Use the <Link to="/quote" data-testid="contact-cta-quote">quote form</Link>.
            </p>
          </div>
          <div>
            <div className="erf-public-eyebrow">Send us a note</div>
            {success ? (
              <div className="erf-public-empty" data-testid="contact-success"
                   style={{ borderStyle: "solid", background: "var(--exec-bg-alt)", marginTop: 16 }}>
                <h3>Thank you.</h3>
                <p>{success.message || "We've received your submission — we'll be in touch."}</p>
                {success.lead_number && (
                  <div style={{ fontSize: 12, color: "var(--exec-body)", marginTop: 6 }}>
                    Reference: <code data-testid="contact-success-ref">{success.lead_number}</code>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="erf-public-form" data-testid="contact-form" style={{ marginTop: 16 }}>
                <div className="erf-public-form-row-2">
                  <div>
                    <label>Your name</label>
                    <input type="text" value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="contact-name" />
                  </div>
                  <div>
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="contact-email" />
                  </div>
                </div>
                <div className="erf-public-form-row-2">
                  <div>
                    <label>Phone (optional)</label>
                    <input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contact-phone" />
                  </div>
                  <div>
                    <label>Subject (optional)</label>
                    <input type="text" value={form.subject} onChange={(e) => upd("subject", e.target.value)} data-testid="contact-subject" />
                  </div>
                </div>
                <div>
                  <label>Message</label>
                  <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} required data-testid="contact-message" placeholder="Tell us how we can help." />
                </div>
                <PublicIntake formSlug="qa-contact-form" onIdsChange={setAttachmentIds} />
                <div className="erf-public-honeypot" aria-hidden="true">
                  <label htmlFor="contact-hp">Leave blank</label>
                  <input id="contact-hp" type="text" tabIndex={-1} autoComplete="off"
                          value={form.honeypot} onChange={(e) => upd("honeypot", e.target.value)} />
                </div>
                {/* R1e-Freeze-A2P — Optional SMS opt-in. Never preselected.
                    Kept SEPARATE from any Terms acceptance requirement. */}
                <div
                  data-testid="contact-sms-consent-block"
                  style={{
                    marginTop: 8, paddingTop: 16,
                    borderTop: "1px solid var(--exec-border)",
                  }}
                >
                  <label
                    htmlFor="contact-sms-consent"
                    style={{
                      display: "flex", gap: 12, alignItems: "flex-start",
                      cursor: "pointer", fontSize: 13, lineHeight: 1.55,
                      color: "var(--exec-body)",
                    }}
                  >
                    <input
                      id="contact-sms-consent"
                      name="sms_consent_granted"
                      type="checkbox"
                      defaultChecked={false}
                      checked={form.sms_consent_granted}
                      onChange={(e) => upd("sms_consent_granted", e.target.checked)}
                      data-testid="contact-sms-consent-checkbox"
                      style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, cursor: "pointer" }}
                    />
                    <span data-testid="contact-sms-consent-disclosure">
                      By checking this box, I agree to receive SMS messages from
                      ERF ENTERPRISES LLC concerning quote requests, appointments,
                      project updates, account notifications and customer support.
                      Message frequency varies. Message and data rates may apply.
                      Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help.
                      Consent is not a condition of purchase. See our{" "}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="contact-sms-privacy-link">
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="contact-sms-terms-link">
                        Terms and Conditions
                      </a>
                      .
                    </span>
                  </label>
                  <p
                    data-testid="contact-sms-optional-helper"
                    style={{ marginTop: 8, fontSize: 11, color: "var(--exec-body)", opacity: 0.75, fontStyle: "italic" }}
                  >
                    Optional. You can submit this form without agreeing to SMS.
                  </p>
                </div>
                {error && <div style={{ color: "#B91C1C", fontSize: 14 }} data-testid="contact-error">{error}</div>}
                <button type="submit" className="erf-public-cta" disabled={submitting}
                        data-testid="contact-submit"
                        style={{ border: 0, minHeight: 44, cursor: submitting ? "wait" : "pointer" }}>
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <p style={{ fontSize: 12, color: "var(--exec-body)", lineHeight: 1.55 }}>
                  Your submission is stored with an audit-tracked reference number. We do not share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
